import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useT } from '../utils/translate';
import axios from 'axios';
import toast from 'react-hot-toast';
import { fetchTasks, upsertTask, removeTask as removeTaskAction } from '../store/tasksSlice';
import {
  ArrowLeft, Loader2, Send, MessageSquare, Trash2, Pencil,
  Plus, X, Check, ChevronDown, Upload, FileText, MoreVertical,
  User, DollarSign, Kanban, Phone, CheckSquare2,
  Mail, AlertCircle, ExternalLink, Layers, Calendar,
  PhoneIncoming, PhoneOutgoing, PhoneMissed, Archive, ArchiveRestore, Trophy, XCircle, RotateCcw,
} from 'lucide-react';
import { getSocket } from '../utils/socket';
import { usePermissions } from '../utils/permissions';
import CallButton from '../components/CallButton';
import CallRecordingPlayer from '../components/CallRecordingPlayer';
import { CONTACT_TYPE_ICON, OUTCOME_COLOR, OUTCOME_TAGS, outcomeLabelKey, contactTypeLabelKey } from '../utils/dealOutcome';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

// ─── Floating dropdown (portal, fixed-positioned, no clipping) ───────────────

function FloatingDropdown({ anchorRef, open, onClose, children, minWidth = 240, placement = 'vertical', estimatedHeight = 280 }) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();

    // 'side' — panel bilan yonma-yon ochiladi (o'ngga, joy yetmasa chapga).
    // Uzun ro'yxat pastda bo'lganda "yuqoriga sig'maydi" hisobidan noto'g'ri
    // ekranning tepasiga sakrab ketishining oldini oladi (vertikal flip yo'q).
    // maxH chaqiruvchidan keladi (haqiqiy variantlar soniga qarab) — aks holda
    // 1-2 ta variantli qisqa ro'yxat ham har doim 280px joy band qilingandek
    // hisoblanib, tugma yonidan emas, ancha yuqoridan chiqib ketardi.
    if (placement === 'side') {
      const panelW = Math.max(minWidth, 200);
      const maxH = estimatedHeight;
      const openLeft = window.innerWidth - rect.right < panelW + 12 && rect.left > panelW + 12;
      const left = openLeft ? rect.left - panelW - 8 : rect.right + 8;
      const top = Math.min(rect.top, Math.max(8, window.innerHeight - maxH - 8));
      setStyle({ top, left, minWidth: panelW, maxHeight: maxH });
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom;
    const dropH = 260; // approximate max height
    const top = spaceBelow > dropH ? rect.bottom + 4 : rect.top - dropH - 4;
    setStyle({ top, left: rect.left, minWidth: Math.max(minWidth, rect.width) });
  }, [open, anchorRef, minWidth, placement, estimatedHeight]);

  if (!open) return null;
  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 bg-white border border-surface-200 rounded-xl shadow-xl overflow-hidden"
        style={style}
      >
        {children}
      </div>
    </>,
    document.body
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const FIELD_TYPES = [
  { value: 'text',        label: 'Matn' },
  { value: 'textarea',    label: 'Katta matn' },
  { value: 'number',      label: 'Son' },
  { value: 'phone',       label: 'Telefon' },
  { value: 'email',       label: 'Email' },
  { value: 'url',         label: 'URL' },
  { value: 'date',        label: 'Sana' },
  { value: 'boolean',     label: "Ha / Yo'q" },
  { value: 'dropdown',    label: 'Dropdown (1 ta)' },
  { value: 'multiselect', label: "Dropdown (ko'p)" },
];
const NEEDS_OPTIONS = ['dropdown', 'multiselect'];

function uid() { return Math.random().toString(36).slice(2, 9); }
function initials(name = '') { return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase(); }
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function formatTime(iso) {
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  return d.toLocaleDateString('uz-UZ', { day: 'numeric', month: 'short' }) + ', ' + time;
}
function fmtNum(n) { return n ? n.toLocaleString('uz-UZ') : '0'; }
function fmtCallDuration(s) {
  if (!s) return '—';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
}
function fmtCallDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}.${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

function DealCallRow({ call }) {
  const missed = call.status === 'missed' || call.status === 'cancelled';
  const Icon = missed ? PhoneMissed : call.direction === 'out' ? PhoneOutgoing : PhoneIncoming;
  const iconCls = missed ? 'text-red-400' : call.direction === 'out' ? 'text-blue-400' : 'text-emerald-400';
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 border border-surface-100 rounded-xl">
      <Icon className={`w-4 h-4 shrink-0 ${iconCls}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-ink-tertiary">{fmtCallDateTime(call.startedAt || call.createdAt)}</p>
      </div>
      <span className="text-xs text-ink-tertiary shrink-0">{fmtCallDuration(call.duration)}</span>
      <CallRecordingPlayer url={call.recordingUrl} />
    </div>
  );
}

const CALL_STATUS = {
  ringing:   { label: 'Jiringlagan',        cls: 'bg-amber-50 text-amber-600' },
  active:    { label: 'Faol',               cls: 'bg-emerald-50 text-emerald-600' },
  completed: { label: 'Gaplashildi',        cls: 'bg-surface-100 text-ink-tertiary' },
  missed:    { label: "O'tkazib yuborilgan", cls: 'bg-red-50 text-red-500' },
  cancelled: { label: 'Bekor qilindi',      cls: 'bg-surface-100 text-ink-disabled' },
};

// Faoliyat tarixida (izohlar bilan bir qatorda, xronologik tartibda) ko'rinadigan
// qo'ng'iroq — Kontakt sahifasidagi CallItem bilan bir xil ko'rinish.
function CallItem({ call }) {
  const isMissed = call.status === 'missed' || call.status === 'cancelled';
  const isOut    = call.direction === 'out';
  const Icon = isMissed ? PhoneMissed : isOut ? PhoneOutgoing : PhoneIncoming;
  const iconCls = isMissed ? 'text-red-400' : isOut ? 'text-blue-400' : 'text-emerald-500';
  const st = CALL_STATUS[call.status] || CALL_STATUS.completed;
  return (
    <div className="flex items-start gap-2.5 py-1">
      <div className="w-7 h-7 rounded-full bg-surface-100 flex items-center justify-center shrink-0 mt-1">
        <Icon className={`w-3.5 h-3.5 ${iconCls}`} />
      </div>
      <div className="flex-1 min-w-0 bg-white rounded-2xl rounded-tl-sm px-3.5 py-2.5 border border-surface-100">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-xs font-semibold text-ink">
            {isOut ? 'Chiquvchi' : 'Kiruvchi'} qo'ng'iroq
          </span>
          <span className={`text-[10px] font-medium px-1.5 py-px rounded-full ${st.cls}`}>{st.label}</span>
          {call.duration ? <span className="text-[11px] text-ink-tertiary font-mono">{fmtCallDuration(call.duration)}</span> : null}
        </div>
        <p className="text-[11px] text-ink-tertiary">{fmtCallDateTime(call.startedAt || call.createdAt)}{call.ext ? ` · Ext: ${call.ext}` : ''}</p>
        {call.recordingUrl && <div className="mt-1.5"><CallRecordingPlayer url={call.recordingUrl} /></div>}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CustomFieldInput({ field, value, onChange }) {
  const [msOpen, setMsOpen] = useState(false);
  const msAnchorRef = useRef(null);

  if (field.type === 'boolean') {
    return (
      <button type="button" onClick={() => onChange(!value)}
        className={`relative w-10 h-5 rounded-full transition-colors shrink-0 ${value ? 'bg-primary-500' : 'bg-surface-200'}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${value ? 'left-5' : 'left-0.5'}`} />
      </button>
    );
  }
  if (field.type === 'textarea') {
    return (
      <textarea rows={2}
        className="flex-1 min-w-0 text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 placeholder:text-ink-disabled resize-none leading-relaxed"
        placeholder="—" value={value || ''} onChange={e => onChange(e.target.value)} />
    );
  }
  if (field.type === 'dropdown') {
    const opts = Array.isArray(field.options) ? field.options : [];
    return (
      <div className="relative flex-1 min-w-0">
        <select className="w-full text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 appearance-none pr-5 cursor-pointer"
          value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">— Tanlang</option>
          {opts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary pointer-events-none" />
      </div>
    );
  }
  if (field.type === 'multiselect') {
    const opts = Array.isArray(field.options) ? field.options : [];
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="relative flex-1 min-w-0">
        <button ref={msAnchorRef} type="button" onClick={() => setMsOpen(v => !v)}
          className="w-full flex items-center justify-between gap-2 text-left text-sm bg-transparent border-0 outline-none cursor-pointer py-0.5">
          <span className={`flex-1 min-w-0 truncate ${selected.length ? 'text-ink' : 'text-ink-disabled'}`}>
            {selected.length > 0 ? selected.join(', ') : '— Tanlang'}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-ink-tertiary shrink-0 transition-transform ${msOpen ? 'rotate-180' : ''}`} />
        </button>
        <FloatingDropdown anchorRef={msAnchorRef} open={msOpen} onClose={() => setMsOpen(false)} placement="side" minWidth={200}
          estimatedHeight={Math.min(280, Math.max(60, opts.length * 36 + 16))}>
          <div className="max-h-56 overflow-y-auto py-1">
            {opts.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-3 py-2 text-sm text-ink hover:bg-surface-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(opt)}
                  onChange={() => onChange(selected.includes(opt) ? selected.filter(v => v !== opt) : [...selected, opt])}
                  className="rounded border-surface-300" />
                {opt}
              </label>
            ))}
            {opts.length === 0 && <p className="px-3 py-3 text-xs text-ink-tertiary text-center">Variant yo'q</p>}
          </div>
        </FloatingDropdown>
      </div>
    );
  }
  return (
    <input
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
      className="flex-1 min-w-0 text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 placeholder:text-ink-disabled"
      placeholder="—" value={value || ''} onChange={e => onChange(e.target.value)} />
  );
}

function SystemEvent({ activity }) {
  const colorMap = {
    created:        'text-ink-tertiary',
    stage_changed:  'text-primary-600',
    funnel_changed: 'text-amber-600',
    won:            'text-emerald-600',
    lost:           'text-red-500',
  };
  const labelMap = { created: 'Yaratildi' };
  const text = labelMap[activity.type] || activity.text || '';
  const color = colorMap[activity.type] || 'text-ink-tertiary';
  const by = activity.createdBy?.name;
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1 h-px bg-surface-100" />
      <span className={`text-[11px] font-medium whitespace-nowrap px-1 ${color}`}>
        {text}{by ? ` — ${by}` : ''} · {formatTime(activity.createdAt)}
      </span>
      <div className="flex-1 h-px bg-surface-100" />
    </div>
  );
}

function CommunicationItem({ activity, onDelete, currentUserId }) {
  const t = useT();
  const name  = activity.createdBy?.name || 'Foydalanuvchi';
  const isOwn = activity.createdBy?._id === currentUserId || activity.createdBy === currentUserId;
  const meta  = activity.meta || {};
  const ContactIcon = CONTACT_TYPE_ICON[meta.contactType] || Phone;
  return (
    <div className="flex items-start gap-2.5 py-1 group/note">
      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[10px] font-bold text-primary-600">{initials(name)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-xs font-semibold text-ink">{name}</span>
          <span className="text-[10px] text-ink-tertiary">{formatTime(activity.createdAt)}</span>
        </div>
        <div className="relative">
          <div className="bg-white border border-surface-200 shadow-sm rounded-xl rounded-tl-sm px-3 py-2 text-sm text-ink leading-relaxed">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <span className="flex items-center gap-1 text-[10px] font-medium text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">
                <ContactIcon className="w-2.5 h-2.5" /> {t(contactTypeLabelKey(meta.contactType)) || meta.contactType}
              </span>
              {meta.contactType === 'call' && meta.duration > 0 && (
                <span className="text-[10px] text-ink-tertiary">{Math.round(meta.duration / 60)} {t('deals.durationLabel')}</span>
              )}
              {meta.outcomeTag && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${OUTCOME_COLOR[meta.outcomeTag] || 'bg-surface-100 text-ink-secondary'}`}>
                  {t(outcomeLabelKey(meta.outcomeTag)) || meta.outcomeTag}
                </span>
              )}
            </div>
            <div className="whitespace-pre-wrap break-words">{activity.text}</div>
          </div>
          {isOwn && (
            <button onClick={() => onDelete(activity._id)}
              className="absolute -top-1 -right-1 opacity-0 group-hover/note:opacity-100 transition-opacity w-5 h-5 bg-white border border-surface-100 rounded-full flex items-center justify-center text-ink-tertiary hover:text-red-500 hover:border-red-200">
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NoteItem({ activity, onDelete, currentUserId }) {
  const name  = activity.createdBy?.name || 'Foydalanuvchi';
  const isOwn = activity.createdBy?._id === currentUserId || activity.createdBy === currentUserId;
  return (
    <div className="flex items-start gap-2.5 py-1 group/note">
      <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-[10px] font-bold text-primary-600">{initials(name)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-xs font-semibold text-ink">{name}</span>
          <span className="text-[10px] text-ink-tertiary">{formatTime(activity.createdAt)}</span>
        </div>
        <div className="relative">
          <div className="bg-white border border-surface-200 shadow-sm rounded-xl rounded-tl-sm px-3 py-2 text-sm text-ink leading-relaxed whitespace-pre-wrap break-words">
            {activity.text}
          </div>
          {isOwn && (
            <button onClick={() => onDelete(activity._id)}
              className="absolute -top-1 -right-1 opacity-0 group-hover/note:opacity-100 transition-opacity w-5 h-5 bg-white border border-surface-100 rounded-full flex items-center justify-center text-ink-tertiary hover:text-red-500 hover:border-red-200">
              <Trash2 className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DealDetailPage({ funnelId, dealId }) {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const t = useT();
  const currency  = useSelector(s => s.auth.user?.organization?.currency || 'UZS');
  const meId      = useSelector(s => s.auth.user?._id || s.auth.user?.id);
  const [allFunnelNames, setAllFunnelNames] = useState([]);
  const taskStages = useSelector(s => s.tasks.stages);
  const isNew     = dealId === 'new';
  const perm = usePermissions();
  const canSave      = isNew ? perm.can('funnels', 'create') : perm.can('funnels', 'edit');
  const canDeleteDeal = perm.can('funnels', 'delete');
  const canCreateTask = perm.can('tasks', 'create');
  const canDeleteTask = perm.can('tasks', 'delete');

  // Core data
  const [funnel,   setFunnel]   = useState(null);
  const [deal,     setDeal]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [tab,      setTab]      = useState('main');

  // Vazifa yaratish (shu lead/dealga bog'langan)
  const [showTaskModal,   setShowTaskModal]   = useState(false);
  const [taskSaving,      setTaskSaving]      = useState(false);
  const [newTaskTitle,    setNewTaskTitle]    = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState('');
  const [newTaskDueDate,  setNewTaskDueDate]  = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('normal');
  // Shu lidga bog'langan vazifalar ro'yxati
  const [dealTasks,     setDealTasks]     = useState([]);
  const [dealTasksLoading, setDealTasksLoading] = useState(false);

  // Form state
  const [title,      setTitle]      = useState('');
  const [stageId,    setStageId]    = useState('');
  const [value,      setValue]      = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [contact,    setContact]    = useState('');
  const [source,     setSource]     = useState('');
  const [notes,      setNotes]      = useState('');
  const notesRef = useRef(null);

  // Custom fields
  const [customFieldValues,    setCustomFieldValues]    = useState({});
  const [origCustomFieldValues, setOrigCustomFieldValues] = useState({});
  const [orgSections,          setOrgSections]          = useState([]);
  const [origOrgSections,      setOrigOrgSections]      = useState([]);
  const [activeSectionId,      setActiveSectionId]      = useState(null);
  const [addingSection,        setAddingSection]        = useState(false);
  const [newSectionName,       setNewSectionName]       = useState('');
  const [addingField,          setAddingField]          = useState(false);
  const [newField,             setNewField]             = useState({ key: '', type: 'text', options: [] });
  const [newOption,            setNewOption]            = useState('');
  const [editingFieldId,       setEditingFieldId]       = useState(null);
  const [editField,            setEditField]            = useState({ key: '', type: 'text', options: [] });
  const [editOption,           setEditOption]           = useState('');

  // Aux data
  const [contacts, setContacts] = useState([]);
  const [users,    setUsers]    = useState([]);
  const [dealSources, setDealSources] = useState([]);
  const [closeReasons, setCloseReasons] = useState({ won: [], lost: [] });
  const [linkedContact,        setLinkedContact]        = useState(null);
  const [linkedContactLoading, setLinkedContactLoading] = useState(false);
  const [showAssignedPicker, setShowAssignedPicker] = useState(false);
  const [showContactPicker,  setShowContactPicker]  = useState(false);
  const [contactSearch,      setContactSearch]      = useState('');
  const [showNewContact,     setShowNewContact]     = useState(false);
  const [newContact,         setNewContact]         = useState({ name: '', phone: '', email: '' });
  const [savingContact,      setSavingContact]      = useState(false);
  const [confirmDelete,      setConfirmDelete]      = useState(false);
  const [showMenu,           setShowMenu]           = useState(false);

  // Arxivlash
  const [showArchiveReason,  setShowArchiveReason]  = useState(false);
  const [archiveReasonInput, setArchiveReasonInput] = useState('');
  const [archiving,          setArchiving]          = useState(false);

  // Sdelkani yopish (G'olib/Yo'qotilgan) — bosqichdan mustaqil, umumiy amal
  const [closeModal,    setCloseModal]    = useState(null); // 'won' | 'lost' | null
  const [closeReasonInput, setCloseReasonInput] = useState('');
  const [closing,       setClosing]       = useState(false);

  // Boshqa varonkaga o'tkazish
  const [showMoveFunnel,     setShowMoveFunnel]     = useState(false);
  const [moveFunnelId,       setMoveFunnelId]       = useState('');
  const [moveStageId,        setMoveStageId]        = useState('');
  const [moveReason,         setMoveReason]         = useState('');
  const [moving,             setMoving]             = useState(false);

  // Files
  const [files,        setFiles]        = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Activities
  const [activities,   setActivities]  = useState([]);
  const [actLoading,   setActLoading]  = useState(false);
  const [noteText,     setNoteText]    = useState('');
  const [noteSending,  setNoteSending] = useState(false);
  const [noteMode,        setNoteMode]        = useState('note'); // 'note' | 'communication'
  const [commContactType, setCommContactType] = useState('call'); // call | message | meeting
  const [commDuration,    setCommDuration]    = useState('');     // daqiqa
  const [commOutcome,     setCommOutcome]     = useState('');

  // Deal calls (statistika uchun)
  const [dealCalls,    setDealCalls]   = useState([]);

  const bottomRef        = useRef(null);
  const textareaRef      = useRef(null);
  const contactAnchorRef = useRef(null);
  const assignedAnchorRef = useRef(null);

  // Originals for dirty check
  const [origTitle,      setOrigTitle]      = useState('');
  const [origStageId,    setOrigStageId]    = useState('');
  const [origValue,      setOrigValue]      = useState('');
  const [origAssignedTo, setOrigAssignedTo] = useState('');
  const [origContact,    setOrigContact]    = useState('');
  const [origSource,     setOrigSource]     = useState('');

  const isDirty = isNew || (
    title !== origTitle ||
    stageId !== origStageId ||
    String(value) !== String(origValue) ||
    assignedTo !== origAssignedTo ||
    contact !== origContact ||
    source !== origSource ||
    JSON.stringify(customFieldValues) !== JSON.stringify(origCustomFieldValues) ||
    JSON.stringify(orgSections) !== JSON.stringify(origOrgSections)
  );

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [fRes, cRes, uRes, fieldsRes, sourcesRes, nRes, closeReasonsRes] = await Promise.all([
          isNew
            ? axios.get(`${API}/funnels/${funnelId}`)
            : axios.get(`${API}/funnels/${funnelId}/deals/${dealId}`),
          axios.get(`${API}/contacts?limit=200`),
          axios.get(`${API}/organization/users`),
          axios.get(`${API}/organization/deal-fields`),
          axios.get(`${API}/organization/deal-sources`),
          axios.get(`${API}/funnels/names`),
          axios.get(`${API}/organization/close-reasons`).catch(() => ({ data: {} })),
        ]);
        setDealSources(sourcesRes.data.sources || []);
        setAllFunnelNames(nRes.data.funnels || []);
        setCloseReasons(closeReasonsRes.data.closeReasons || { won: [], lost: [] });

        const funnelData = fRes.data.funnel;
        setFunnel(funnelData);

        const secs = Array.isArray(fieldsRes.data.sections) ? fieldsRes.data.sections : [];
        setOrgSections(secs);
        setOrigOrgSections(JSON.parse(JSON.stringify(secs)));
        if (secs.length > 0) setActiveSectionId(secs[0].id);

        setContacts(cRes.data.contacts || []);
        setUsers(uRes.data.users || []);

        if (!isNew) {
          const d = fRes.data.deal;
          setDeal(d);
          setTitle(d.title || '');
          setStageId(String(d.stageId || ''));
          setValue(d.value ?? '');
          setAssignedTo(d.assignedTo?._id || d.assignedTo || '');
          setContact(d.contact?._id || d.contact || '');
          setSource(d.source || '');
          setNotes(d.notes || '');
          setFiles(d.files || []);
          const vals = d.customFieldValues || {};
          setCustomFieldValues(vals);
          setCustomFieldValues(vals);
          setOrigCustomFieldValues(JSON.parse(JSON.stringify(vals)));
          // originals
          setOrigTitle(d.title || '');
          setOrigStageId(String(d.stageId || ''));
          setOrigValue(d.value ?? '');
          setOrigAssignedTo(d.assignedTo?._id || d.assignedTo || '');
          setOrigContact(d.contact?._id || d.contact || '');
          setOrigSource(d.source || '');
        } else {
          // New deal: first stage + current user as default assignee
          const firstStage = funnelData?.stages?.[0];
          setStageId(firstStage ? String(firstStage._id) : '');
          setOrigStageId(firstStage ? String(firstStage._id) : '');
          if (meId) setAssignedTo(meId);
        }
      } catch {
        toast.error('Yuklanmadi');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [funnelId, dealId, isNew, navigate, meId]);

  // Activities
  const loadActivities = useCallback(async () => {
    if (isNew) return;
    setActLoading(true);
    try {
      const r = await axios.get(`${API}/funnels/${funnelId}/deals/${dealId}/activities`);
      setActivities(r.data.activities || []);
    } catch { /**/ }
    finally { setActLoading(false); }
  }, [funnelId, dealId, isNew]);

  useEffect(() => { loadActivities(); }, [loadActivities]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [activities]);

  // Real-time sync: boshqa foydalanuvchi shu sdelkaga izoh qo'shsa/o'chirsa
  // yoki bosqichini o'zgartirsa — bu yerda ochib turgan odamda ham darhol ko'rinadi.
  useEffect(() => {
    if (isNew) return;
    const socket = getSocket();
    const onActivityCreated = ({ dealId: did, activity }) => {
      if (String(did) !== String(dealId)) return;
      setActivities(prev => prev.some(a => a._id === activity._id) ? prev : [...prev, activity]);
    };
    const onActivityDeleted = ({ dealId: did, activityId }) => {
      if (String(did) !== String(dealId)) return;
      setActivities(prev => prev.filter(a => a._id !== activityId));
    };
    socket.on('deal:activity-created', onActivityCreated);
    socket.on('deal:activity-deleted', onActivityDeleted);
    return () => {
      socket.off('deal:activity-created', onActivityCreated);
      socket.off('deal:activity-deleted', onActivityDeleted);
    };
  }, [dealId, isNew]);

  // Izoh maydoni — matn ko'p bo'lsa balandligi avtomatik o'sadi (o'qish qulay bo'lishi uchun)
  useEffect(() => {
    if (!notesRef.current) return;
    notesRef.current.style.height = 'auto';
    notesRef.current.style.height = Math.min(notesRef.current.scrollHeight, 320) + 'px';
  }, [notes]);

  // Biriktirilgan kontaktning to'liq ma'lumotini tortib olish (200 talik ro'yxatga tayanmasdan)
  useEffect(() => {
    if (!contact) { setLinkedContact(null); return; }
    setLinkedContactLoading(true);
    axios.get(`${API}/contacts/${contact}`)
      .then(r => setLinkedContact(r.data.contact))
      .catch(() => setLinkedContact(null))
      .finally(() => setLinkedContactLoading(false));
  }, [contact]);

  // Qo'ng'iroqlarni deal va/yoki kontakt bo'yicha yuklash — Statistika tabidagi
  // hisoblagichlar uchun ham, Asosiy tabdagi faoliyat tarixiga izohlar bilan bir
  // qatorda (xronologik) aralashtirib ko'rsatish uchun ham kerak.
  useEffect(() => {
    if (isNew) return;
    const promises = [];
    if (dealId) promises.push(axios.get(`${API}/atc/calls`, { params: { deal: dealId, limit: 200 } }).then(r => r.data.calls || []));
    if (contact) promises.push(axios.get(`${API}/atc/calls`, { params: { contact, limit: 200 } }).then(r => r.data.calls || []));
    if (promises.length === 0) { setDealCalls([]); return; }
    Promise.all(promises).then(results => {
      const merged = Object.values(
        results.flat().reduce((acc, c) => { acc[c._id] = c; return acc; }, {})
      );
      setDealCalls(merged);
    }).catch(() => {});
  }, [contact, dealId, isNew]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) { toast.error('Sarlavha kiritilishi shart'); return; }
    if (!stageId) { toast.error(t('deals.stage') + '!'); return; }
    setSaving(true);
    try {
      const orgChanged = JSON.stringify(orgSections) !== JSON.stringify(origOrgSections);
      if (orgChanged) {
        await axios.put(`${API}/organization/deal-fields`, { sections: orgSections });
        setOrigOrgSections(JSON.parse(JSON.stringify(orgSections)));
      }

      const payload = {
        title: title.trim(), stageId, value: Number(value) || 0,
        assignedTo: assignedTo || null, contact: contact || null,
        source: source || '',
        notes, customFieldValues,
      };

      if (isNew) {
        const res = await axios.post(`${API}/funnels/${funnelId}/deals`, payload);
        toast.success(t('deals.created'));
        navigate(`/funnel/${funnelId}/deal/${res.data.deal._id}`, { replace: true });
      } else {
        await axios.put(`${API}/funnels/${funnelId}/deals/${dealId}`, payload);
        setOrigTitle(title);
        setOrigStageId(stageId);
        setOrigValue(value);
        setOrigAssignedTo(assignedTo);
        setOrigContact(contact);
        setOrigSource(source);
        setOrigCustomFieldValues(JSON.parse(JSON.stringify(customFieldValues)));
        toast.success('Saqlandi');
        loadActivities();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || t('deals.loadError'));
    } finally {
      setSaving(false);
    }
  };

  // "O'zimga olish" — pool'dagi (mas'ulsiz) lidni o'ziga biriktirib olish
  const handleClaim = async () => {
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/${dealId}/claim`);
      setAssignedTo(res.data.deal.assignedTo?._id || res.data.deal.assignedTo || '');
      setOrigAssignedTo(res.data.deal.assignedTo?._id || res.data.deal.assignedTo || '');
      toast.success("O'zingizga oldingiz");
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xato');
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/funnels/${funnelId}/deals/${dealId}`);
      toast.success("O'chirildi");
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.message || t('deals.loadError'));
    }
  };

  // ── Arxivlash / arxivdan qaytarish ─────────────────────────────────────────
  const handleArchive = async () => {
    if (!archiveReasonInput.trim()) return;
    setArchiving(true);
    try {
      await axios.post(`${API}/funnels/${funnelId}/deals/bulk-archive`, {
        dealIds: [dealId], archived: true, reason: archiveReasonInput.trim(),
      });
      toast.success('Arxivlandi');
      setShowArchiveReason(false);
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xato');
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    setArchiving(true);
    try {
      await axios.post(`${API}/funnels/${funnelId}/deals/bulk-archive`, {
        dealIds: [dealId], archived: false,
      });
      setDeal(prev => prev ? { ...prev, archived: false, archiveReason: '' } : prev);
      toast.success('Arxivdan qaytarildi');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xato');
    } finally {
      setArchiving(false);
    }
  };

  // ── Sdelkani yopish (bosqichdan mustaqil, umumiy amal) ──────────────────────
  const handleCloseDeal = async (targetStatus) => {
    setClosing(true);
    try {
      const body = { status: targetStatus, closeReason: closeReasonInput.trim() };
      // Voronkada shu holat uchun "G'olib"/"Yo'qotilgan" deb belgilangan bosqich
      // bo'lsa, sdelkani ham o'sha bosqichga ko'chiramiz — Kanban taxtasi bilan
      // mos kelib turishi uchun (aks holda joriy ustunida qolib, faqat statusi o'zgaradi).
      const targetStage = funnel?.stages?.find(s => targetStatus === 'won' ? s.isWon : s.isLost);
      if (targetStage) body.stageId = String(targetStage._id);
      const res = await axios.put(`${API}/funnels/${funnelId}/deals/${dealId}`, body);
      setDeal(res.data.deal);
      if (targetStage) setStageId(String(targetStage._id));
      toast.success(targetStatus === 'won' ? "G'olib deb yopildi" : "Yo'qotilgan deb yopildi");
      setCloseModal(null);
      setCloseReasonInput('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xato');
    } finally {
      setClosing(false);
    }
  };

  const handleReopenDeal = async () => {
    setClosing(true);
    try {
      const res = await axios.put(`${API}/funnels/${funnelId}/deals/${dealId}`, { status: 'active' });
      setDeal(res.data.deal);
      toast.success('Qayta ochildi');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xato');
    } finally {
      setClosing(false);
    }
  };

  // ── Yangi kontakt yaratish (kontakt tanlash ro'yxati ichida) ─────────────
  const handleCreateContact = async () => {
    if (!newContact.name.trim() || savingContact) return;
    setSavingContact(true);
    try {
      const res = await axios.post(`${API}/contacts`, { ...newContact, assignedTo: assignedTo || undefined });
      setContacts(prev => [res.data.contact, ...prev]);
      setContact(res.data.contact._id);
      toast.success(t('funnel.contactCreated'));
      setShowNewContact(false);
      setNewContact({ name: '', phone: '', email: '' });
      setShowContactPicker(false);
      setContactSearch('');
    } catch (e) {
      const duplicate = e.response?.data?.duplicate;
      if (duplicate) {
        setContacts(prev => prev.some(c => c._id === duplicate._id) ? prev : [duplicate, ...prev]);
        setContact(duplicate._id);
        toast.success(`${t('funnel.contactExists')}: ${duplicate.name}`);
        setShowNewContact(false);
        setNewContact({ name: '', phone: '', email: '' });
        setShowContactPicker(false);
        setContactSearch('');
      } else {
        toast.error(e.response?.data?.message || t('funnel.loadError'));
      }
    } finally {
      setSavingContact(false);
    }
  };

  // ── Boshqa varonkaga o'tkazish ───────────────────────────────────────────
  const moveTargetFunnels = allFunnelNames.filter(f => String(f._id) !== String(funnelId));
  const moveTargetFunnel  = moveTargetFunnels.find(f => String(f._id) === String(moveFunnelId));

  const openMoveFunnel = () => {
    setShowMenu(false);
    setMoveFunnelId('');
    setMoveStageId('');
    setMoveReason('');
    setShowMoveFunnel(true);
  };

  const handleMoveFunnel = async () => {
    if (!moveFunnelId || !moveStageId) return;
    setMoving(true);
    try {
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/${dealId}/move`, {
        targetFunnelId: moveFunnelId, targetStageId: moveStageId, reason: moveReason.trim(),
      });
      toast.success(t('deals.moveSuccess'));
      setShowMoveFunnel(false);
      navigate(`/funnel/${moveFunnelId}/deal/${res.data.deal._id}`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.message || t('deals.loadError'));
    } finally {
      setMoving(false);
    }
  };

  // ── File upload ──────────────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setUploadingFile(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await axios.post(`${API}/upload/file`, form);
      const { url, name: fileName, size, type } = uploadRes.data;
      const res = await axios.post(`${API}/funnels/${funnelId}/deals/${dealId}/files`, { name: fileName, url, size, type });
      setFiles(res.data.files);
      toast.success('Fayl yuklandi');
    } catch {
      toast.error('Yuklashda xato');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      const res = await axios.delete(`${API}/funnels/${funnelId}/deals/${dealId}/files/${fileId}`);
      setFiles(res.data.files);
    } catch {
      toast.error(t('deals.loadError'));
    }
  };

  // ── Task (shu leadga bog'langan vazifa) ─────────────────────────────────────
  const loadDealTasks = useCallback(async () => {
    if (isNew) return;
    setDealTasksLoading(true);
    try {
      const res = await axios.get(`${API}/tasks`, { params: { deal: dealId, limit: 100 } });
      setDealTasks(res.data.tasks || []);
    } catch { /* jim - vazifalar ro'yxati ikkinchi darajali ma'lumot */ }
    finally { setDealTasksLoading(false); }
  }, [dealId, isNew]);

  useEffect(() => { loadDealTasks(); }, [loadDealTasks]);

  const openTaskModal = () => {
    dispatch(fetchTasks());
    setNewTaskTitle(title || '');
    setNewTaskDescription('');
    setNewTaskAssignee(assignedTo || '');
    setNewTaskDueDate('');
    setNewTaskPriority('normal');
    setShowTaskModal(true);
  };

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) { toast.error(t('tasks.titleRequired')); return; }
    const firstStage = taskStages[0];
    if (!firstStage) { toast.error(t('deals.loadError')); return; }
    setTaskSaving(true);
    try {
      const res = await axios.post(`${API}/tasks`, {
        title:       newTaskTitle.trim(),
        description: newTaskDescription.trim(),
        stageId:    String(firstStage._id || firstStage.name),
        assignedTo: newTaskAssignee || null,
        dueDate:    newTaskDueDate ? new Date(newTaskDueDate).toISOString() : null,
        priority:   newTaskPriority,
        contact:    linkedContact?._id || null,
        deal:       dealId,
      });
      dispatch(upsertTask(res.data.task));
      setDealTasks(prev => [res.data.task, ...prev]);
      toast.success(t('tasks.created'));
      setShowTaskModal(false);
    } catch (e) {
      toast.error(e.response?.data?.message || t('tasks.error'));
    } finally {
      setTaskSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm(t('tasks.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setDealTasks(prev => prev.filter(x => x._id !== taskId));
      dispatch(removeTaskAction(taskId));
      toast.success(t('tasks.deleted'));
    } catch (e) {
      toast.error(e.response?.data?.message || t('tasks.error'));
    }
  };

  // ── Activity ─────────────────────────────────────────────────────────────
  const handleSendNote = async () => {
    if (!noteText.trim() || isNew || noteSending) return;
    setNoteSending(true);
    try {
      const body = noteMode === 'communication'
        ? {
            text: noteText.trim(),
            contactType: commContactType,
            duration: commContactType === 'call' && commDuration ? Math.round(Number(commDuration) * 60) : 0,
            outcomeTag: commOutcome,
          }
        : { text: noteText.trim() };
      const r = await axios.post(`${API}/funnels/${funnelId}/deals/${dealId}/activities`, body);
      setActivities(prev => prev.some(a => a._id === r.data.activity._id) ? prev : [...prev, r.data.activity]);
      setNoteText('');
      setCommDuration('');
      setCommOutcome('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      textareaRef.current?.focus();
    } catch {
      toast.error(t('deals.loadError'));
    } finally {
      setNoteSending(false);
    }
  };

  const handleDeleteNote = async (actId) => {
    try {
      await axios.delete(`${API}/funnels/${funnelId}/deals/${dealId}/activities/${actId}`);
      setActivities(prev => prev.filter(a => a._id !== actId));
    } catch { toast.error(t('deals.loadError')); }
  };

  // ── Org sections ─────────────────────────────────────────────────────────
  // Bo'lim/maydon sozlamalari butun tashkilotga tegishli (bitimga emas) — shuning
  // uchun "Saqlash"/"Qo'shish" bosilishi bilan darhol serverga yoziladi. Avval
  // faqat lokal state o'zgarardi va bitimning asosiy "Saqlash"i bosilmasa yo'qolardi.
  const commitSections = async (next) => {
    setOrgSections(next);
    try {
      await axios.put(`${API}/organization/deal-fields`, { sections: next });
      setOrigOrgSections(JSON.parse(JSON.stringify(next)));
    } catch (err) {
      // State "dirty" bo'lib qoladi — asosiy "Saqlash" orqali qayta urinish mumkin.
      toast.error(err.response?.data?.message || 'Maydon sozlamalari saqlanmadi');
    }
  };
  // Variant inputida yozilgan, lekin "+" bosilmagan matn ham qo'shilsin.
  const withPendingOption = (options, pending) => {
    const p = pending.trim();
    return p && !options.includes(p) ? [...options, p] : options;
  };

  const confirmAddSection = () => {
    const name = newSectionName.trim();
    if (!name) return;
    const sec = { id: uid(), name, fields: [] };
    commitSections([...orgSections, sec]);
    setActiveSectionId(sec.id);
    setNewSectionName('');
    setAddingSection(false);
  };
  const deleteSection = (secId) => {
    const sec = orgSections.find(s => s.id === secId);
    if (!window.confirm(`"${sec?.name}" bo'limini o'chirasizmi? Barcha bitimlardan yo'qoladi.`)) return;
    commitSections(orgSections.filter(s => s.id !== secId));
    setActiveSectionId(orgSections.find(s => s.id !== secId)?.id || null);
    setAddingField(false);
  };
  const deleteCustomField = (secId, fieldId) => {
    const field = orgSections.find(s => s.id === secId)?.fields.find(f => f.id === fieldId);
    if (!window.confirm(`"${field?.key}" maydonini o'chirasizmi? Barcha bitimlardan yo'qoladi.`)) return;
    commitSections(orgSections.map(s => s.id !== secId ? s : { ...s, fields: s.fields.filter(f => f.id !== fieldId) }));
  };
  const confirmAddField = () => {
    const key = newField.key.trim();
    if (!key) return;
    const options = withPendingOption(newField.options, newOption);
    if (NEEDS_OPTIONS.includes(newField.type) && options.length === 0) return;
    const field = { id: uid(), key, type: newField.type, ...(NEEDS_OPTIONS.includes(newField.type) ? { options } : {}) };
    commitSections(orgSections.map(s => s.id !== activeSectionId ? s : { ...s, fields: [...s.fields, field] }));
    setNewField({ key: '', type: 'text', options: [] });
    setNewOption('');
    setAddingField(false);
  };

  const startEditField = (field) => {
    setEditingFieldId(field.id);
    setEditField({ key: field.key, type: field.type, options: field.options ? [...field.options] : [] });
    setEditOption('');
    setAddingField(false);
  };
  const cancelEditField = () => {
    setEditingFieldId(null);
    setEditField({ key: '', type: 'text', options: [] });
    setEditOption('');
  };
  const confirmEditField = (secId) => {
    const key = editField.key.trim();
    if (!key) return;
    const type = editField.type;
    const options = withPendingOption(editField.options, editOption).map(o => o.trim()).filter(Boolean);
    if (NEEDS_OPTIONS.includes(type) && options.length === 0) return;
    commitSections(orgSections.map(s =>
      s.id !== secId ? s : {
        ...s,
        fields: s.fields.map(f => {
          if (f.id !== editingFieldId) return f;
          const { options: _old, ...rest } = f;
          return { ...rest, key, type, ...(NEEDS_OPTIONS.includes(type) ? { options } : {}) };
        }),
      }
    ));
    cancelEditField();
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const activeOrgSection = orgSections.find(s => s.id === activeSectionId);
  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.name.toLowerCase().includes(contactSearch.toLowerCase()) || c.phone?.includes(contactSearch)
  ).slice(0, 30);

  const assignedUser    = users.find(u => u._id === assignedTo);
  const selectedContact = contacts.find(c => c._id === contact);
  const selectedStage   = funnel?.stages?.find(s => String(s._id) === stageId);
  const isLead          = funnel?.stages?.[0] && String(funnel.stages[0]._id) === stageId;

  // ─────────────────────────────────────────────────────────────────────────

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">

      {/* ── Top bar ── */}
      <div className="flex flex-wrap lg:flex-nowrap items-center justify-between gap-x-3 gap-y-2 lg:gap-4 px-4 lg:px-6 py-3 lg:py-4 border-b border-surface-100 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg text-ink-tertiary hover:text-ink hover:bg-surface-100 transition-colors shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-ink-tertiary shrink-0">{funnel?.name}</span>
            <span className="text-ink-disabled">/</span>
            <h1 className="text-lg font-bold text-ink truncate">
              {isNew ? (isLead ? 'Yangi zayavka' : 'Yangi sdelka') : (title || '—')}
            </h1>
          </div>
          {!isNew && isLead && (
            <span className="text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full shrink-0">Zayavka</span>
          )}
          {!isNew && deal?.status === 'won' && (
            <span title={deal.closeReason || ''} className="flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full shrink-0">
              <Trophy className="w-3 h-3" /> G'olib
            </span>
          )}
          {!isNew && deal?.status === 'lost' && (
            <span title={deal.closeReason || ''} className="flex items-center gap-1 text-xs font-medium bg-red-50 text-red-500 border border-red-200 px-2 py-0.5 rounded-full shrink-0">
              <XCircle className="w-3 h-3" /> Yo'qotilgan
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full lg:w-auto justify-end">
          {isDirty && canSave && (
            <button onClick={handleSave} disabled={saving} className="btn-md btn-primary flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('deals.save')}
            </button>
          )}
          {!isNew && (
            <div className="relative">
              <button onClick={() => setShowMenu(m => !m)}
                className="p-2 rounded-lg text-ink-tertiary hover:text-ink hover:bg-surface-100 transition-colors">
                <MoreVertical className="w-5 h-5" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-surface-100 rounded-xl shadow-lg py-1 min-w-[220px]">
                    {moveTargetFunnels.length > 0 && canSave && (
                      <button onClick={openMoveFunnel}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink hover:bg-surface-100 transition-colors">
                        <Layers className="w-4 h-4" /> {t('deals.moveToFunnel')}
                      </button>
                    )}
                    {canSave && !isNew && (deal?.status && deal.status !== 'active' ? (
                      <button onClick={() => { setShowMenu(false); handleReopenDeal(); }} disabled={closing}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink hover:bg-surface-100 transition-colors">
                        <RotateCcw className="w-4 h-4" /> Qayta ochish
                      </button>
                    ) : (
                      <>
                        <button onClick={() => { setShowMenu(false); setCloseReasonInput(''); setCloseModal('won'); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-emerald-600 hover:bg-emerald-50 transition-colors">
                          <Trophy className="w-4 h-4" /> G'olib deb yopish
                        </button>
                        <button onClick={() => { setShowMenu(false); setCloseReasonInput(''); setCloseModal('lost'); }}
                          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors">
                          <XCircle className="w-4 h-4" /> Yo'qotilgan deb yopish
                        </button>
                      </>
                    ))}
                    {canSave && (deal?.archived ? (
                      <button onClick={() => { setShowMenu(false); handleUnarchive(); }} disabled={archiving}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink hover:bg-surface-100 transition-colors">
                        <ArchiveRestore className="w-4 h-4" /> Arxivdan qaytarish
                      </button>
                    ) : (
                      <button onClick={() => { setShowMenu(false); setArchiveReasonInput(''); setShowArchiveReason(true); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink hover:bg-surface-100 transition-colors">
                        <Archive className="w-4 h-4" /> Arxivlash
                      </button>
                    ))}
                    {canDeleteDeal && (
                      <button onClick={() => { setShowMenu(false); setConfirmDelete(true); }}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4" /> {t('deals.delete')}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Vazifa yaratish modal ── */}
      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-4">{t('tasks.newTask')}</p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('tasks.modalTitle')}</label>
            <input
              autoFocus
              className="input w-full mb-3"
              placeholder={t('tasks.titlePlaceholder')}
              value={newTaskTitle}
              onChange={e => setNewTaskTitle(e.target.value)}
            />

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('tasks.description')}</label>
            <textarea
              className="input w-full mb-3 resize-none"
              rows={3}
              placeholder={t('tasks.descPlaceholder')}
              value={newTaskDescription}
              onChange={e => setNewTaskDescription(e.target.value)}
            />

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('tasks.assignee')}</label>
            <select
              className="input w-full mb-3"
              value={newTaskAssignee}
              onChange={e => setNewTaskAssignee(e.target.value)}
            >
              <option value="">{t('tasks.unassigned')}</option>
              {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
            </select>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">{t('tasks.dueDate')}</label>
                <input type="datetime-local" className="input w-full"
                  value={newTaskDueDate} onChange={e => setNewTaskDueDate(e.target.value)} />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1">{t('tasks.priority')}</label>
                <select className="input w-full" value={newTaskPriority} onChange={e => setNewTaskPriority(e.target.value)}>
                  <option value="low">{t('tasks.prioLow')}</option>
                  <option value="normal">{t('tasks.prioMedium')}</option>
                  <option value="high">{t('tasks.prioHigh')}</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowTaskModal(false)} className="btn-md btn-secondary flex-1">{t('tasks.cancel')}</button>
              <button onClick={handleCreateTask} disabled={taskSaving} className="btn-md btn-primary flex-1 flex items-center justify-center gap-2">
                {taskSaving && <Loader2 className="w-4 h-4 animate-spin" />} {t('tasks.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-[340px]">
            <p className="text-base font-semibold text-ink mb-1">{t('deals.deleteConfirm')}</p>
            <p className="text-sm text-ink-tertiary mb-5">
              <span className="font-medium text-ink">{title}</span>
            </p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(false)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button onClick={handleDelete} className="btn-md flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors">{t('deals.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sdelkani yopish modal (G'olib/Yo'qotilgan) ── */}
      {closeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className={`text-base font-semibold mb-1 flex items-center gap-2 ${closeModal === 'won' ? 'text-emerald-600' : 'text-red-500'}`}>
              {closeModal === 'won' ? <Trophy className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {closeModal === 'won' ? "G'olib deb yopish" : "Yo'qotilgan deb yopish"}
            </p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{title}</span>
            </p>
            <label className="block text-xs font-medium text-ink-secondary mb-1">
              Sabab (ixtiyoriy){closeModal === 'lost' && ' — nima uchun yo\'qotildi?'}
            </label>
            {(closeModal === 'won' ? closeReasons.won : closeReasons.lost).length > 0 ? (
              <select
                autoFocus
                className="input w-full mb-5"
                value={closeReasonInput}
                onChange={e => setCloseReasonInput(e.target.value)}
              >
                <option value="">— Sabab tanlanmagan —</option>
                {(closeModal === 'won' ? closeReasons.won : closeReasons.lost).map(r => (
                  <option key={r._id} value={r.name}>{r.name}</option>
                ))}
              </select>
            ) : (
              <textarea
                autoFocus
                className="input w-full mb-5 resize-none"
                rows={3}
                value={closeReasonInput}
                onChange={e => setCloseReasonInput(e.target.value)}
                placeholder={closeModal === 'won' ? 'Masalan: mijoz shartnoma imzoladi' : "Masalan: narx to'g'ri kelmadi"}
              />
            )}
            <div className="flex gap-2">
              <button onClick={() => setCloseModal(null)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={() => handleCloseDeal(closeModal)}
                disabled={closing}
                className={`btn-md flex-1 flex items-center justify-center gap-2 text-white rounded-xl font-medium transition-colors ${
                  closeModal === 'won' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {closing && <Loader2 className="w-4 h-4 animate-spin" />}
                Yopish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Arxivlash sababi modal ── */}
      {showArchiveReason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-1 flex items-center gap-2">
              <Archive className="w-4 h-4" /> Arxivlash
            </p>
            <p className="text-sm text-ink-tertiary mb-4">
              <span className="font-medium text-ink">{title}</span>
            </p>
            <label className="block text-xs font-medium text-ink-secondary mb-1">Sabab (izoh) *</label>
            <textarea
              autoFocus
              className="input w-full mb-5 resize-none"
              rows={3}
              value={archiveReasonInput}
              onChange={e => setArchiveReasonInput(e.target.value)}
              placeholder="Nima uchun arxivlanyapti?"
            />
            <div className="flex gap-2">
              <button onClick={() => setShowArchiveReason(false)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={handleArchive}
                disabled={!archiveReasonInput.trim() || archiving}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {archiving && <Loader2 className="w-4 h-4 animate-spin" />}
                Arxivlash
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Boshqa varonkaga o'tkazish modal ── */}
      {showMoveFunnel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
            <p className="text-base font-semibold text-ink mb-4">{t('deals.moveToFunnel')}</p>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.moveFunnelLabel')}</label>
            <select
              className="input w-full mb-3"
              value={moveFunnelId}
              onChange={e => { setMoveFunnelId(e.target.value); setMoveStageId(''); }}
            >
              <option value="">{t('deals.moveFunnelPlaceholder')}</option>
              {moveTargetFunnels.map(f => <option key={f._id} value={f._id}>{f.name}</option>)}
            </select>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.stage')}</label>
            <select
              className="input w-full mb-5"
              value={moveStageId}
              onChange={e => setMoveStageId(e.target.value)}
              disabled={!moveTargetFunnel}
            >
              <option value="">{t('deals.moveStagePlaceholder')}</option>
              {(moveTargetFunnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>

            <label className="block text-xs font-medium text-ink-secondary mb-1">{t('deals.moveReasonLabel')}</label>
            <textarea
              className="input w-full mb-5 resize-none"
              rows={2}
              value={moveReason}
              onChange={e => setMoveReason(e.target.value)}
              placeholder={t('deals.moveReasonPlaceholder')}
            />

            <div className="flex gap-2">
              <button onClick={() => setShowMoveFunnel(false)} className="btn-md btn-secondary flex-1">{t('deals.cancel')}</button>
              <button
                onClick={handleMoveFunnel}
                disabled={!moveFunnelId || !moveStageId || moving}
                className="btn-md btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {moving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('deals.moveSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 2-column body (telefonda vertikal stack, desktopda yonma-yon) ── */}
      <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden">

        {/* ── LEFT ── */}
        <div className="w-full lg:w-[460px] xl:w-[520px] lg:shrink-0 border-b lg:border-b-0 lg:border-r border-surface-100 flex flex-col bg-white">

          {/* Title */}
          <div className="px-5 pt-5 pb-4 border-b border-surface-100 shrink-0">
            <div className="flex items-center gap-2 group/name">
              <div className="relative flex-1 min-w-0">
                <input
                  className="w-full text-lg font-bold text-ink bg-transparent hover:bg-surface-50 focus:bg-transparent rounded-lg border-b-2 border-transparent focus:border-primary-300 outline-none focus:outline-none focus:ring-0 pr-6 py-0.5 transition-all placeholder:text-ink-disabled cursor-default focus:cursor-text"
                  placeholder={isLead ? 'Mijoz ismi...' : 'Sdelka nomi...'}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
                <Pencil className="absolute right-1 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary opacity-0 group-hover/name:opacity-100 transition-opacity pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 py-3 border-b border-surface-100 shrink-0">
            <div className="flex bg-surface-100 rounded-xl p-1 gap-1">
              {[['main', 'Asosiy'], ['stats', 'Statistika'], ['files', 'Fayllar'], ['settings', 'Sozlama']].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${tab === k ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Tab body */}
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-y-auto px-5 py-4">

            {/* ─── Asosiy tab ─── */}
            {tab === 'main' && (
              <div className="space-y-4">
                {/* Main info card */}
                <div className="border border-surface-100 rounded-xl overflow-hidden divide-y divide-surface-100">

                  {/* Stage */}
                  <div className="flex items-center gap-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <Kanban className="w-3.5 h-3.5 text-ink-tertiary" />
                      <span className="text-sm text-ink">{t('deals.stage')}</span>
                    </div>
                    <div className="relative flex-1 min-w-0">
                      <select className="w-full text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 appearance-none pr-5 cursor-pointer"
                        value={stageId} onChange={e => setStageId(e.target.value)}>
                        {(funnel?.stages || []).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                      </select>
                      <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary pointer-events-none" />
                    </div>
                    {selectedStage && (
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: selectedStage.color || '#94a3b8' }} />
                    )}
                  </div>

                  {/* Value */}
                  <div className="flex items-center gap-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <DollarSign className="w-3.5 h-3.5 text-ink-tertiary" />
                      <span className="text-sm text-ink">{t('deals.value')}</span>
                    </div>
                    <input type="number" min="0"
                      className="flex-1 min-w-0 text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 placeholder:text-ink-disabled"
                      placeholder="0" value={value} onChange={e => setValue(e.target.value)} />
                    <span className="text-xs text-ink-tertiary shrink-0">{currency}</span>
                  </div>

                  {/* Source */}
                  {dealSources.length > 0 && (
                    <div className="flex items-center gap-4 px-4 py-2.5">
                      <div className="flex items-center gap-2 w-32 shrink-0">
                        <Layers className="w-3.5 h-3.5 text-ink-tertiary" />
                        <span className="text-sm text-ink">Manba</span>
                      </div>
                      <select className="flex-1 min-w-0 text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0"
                        value={source} onChange={e => setSource(e.target.value)}>
                        <option value="">— Tanlanmagan —</option>
                        {dealSources.map(s => (
                          <option key={String(s._id)} value={String(s._id)}>{s.name}</option>
                        ))}
                        {/* Import yoki eski yozuvlardan kelgan erkin matn (ro'yxatdagi manbalarga mos kelmasa) — aks holda
                            select hech qaysi optionga mos kelmay "— Tanlanmagan —" ko'rsatib, mavjud qiymatni yashirib qo'yardi */}
                        {source && !dealSources.some(s => String(s._id) === String(source)) && (
                          <option value={source}>{source}</option>
                        )}
                      </select>
                    </div>
                  )}

                  {/* Notes / Izoh */}
                  <div className="flex items-start gap-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 w-32 shrink-0 pt-0.5">
                      <FileText className="w-3.5 h-3.5 text-ink-tertiary" />
                      <span className="text-sm text-ink">Izoh</span>
                    </div>
                    <textarea ref={notesRef} rows={2}
                      className="flex-1 min-w-0 text-sm text-ink bg-transparent border-0 outline-none focus:outline-none focus:ring-0 resize-none leading-relaxed placeholder:text-ink-disabled"
                      style={{ minHeight: '40px' }}
                      placeholder="Izoh yo'q"
                      value={notes} onChange={e => setNotes(e.target.value)} />
                  </div>

                  {/* Contact */}
                  <div className="flex items-center gap-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <Phone className="w-3.5 h-3.5 text-ink-tertiary" />
                      <span className="text-sm text-ink">{t('deals.contact')}</span>
                    </div>
                    <button
                      ref={contactAnchorRef}
                      type="button"
                      onClick={() => { setShowContactPicker(v => !v); setShowAssignedPicker(false); }}
                      className="flex-1 flex items-center justify-between gap-2 min-w-0 hover:text-primary-600 transition-colors"
                    >
                      {(linkedContact || selectedContact) ? (
                        <span className="text-sm truncate">
                          <span className="font-medium text-ink">{(linkedContact || selectedContact).name}</span>
                          {(linkedContact || selectedContact).phone && <span className="text-ink-tertiary ml-2">{(linkedContact || selectedContact).phone}</span>}
                        </span>
                      ) : <span className="text-sm text-ink-disabled">— Tanlang</span>}
                      <ChevronDown className={`w-3.5 h-3.5 text-ink-tertiary shrink-0 transition-transform ${showContactPicker ? 'rotate-180' : ''}`} />
                    </button>
                    <CallButton phone={(linkedContact || selectedContact)?.phone}
                      className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors shrink-0 disabled:opacity-50" />
                    <FloatingDropdown
                      anchorRef={contactAnchorRef}
                      open={showContactPicker}
                      onClose={() => { setShowContactPicker(false); setContactSearch(''); setShowNewContact(false); }}
                    >
                      <div className="p-2 border-b border-surface-100">
                        <input
                          autoFocus
                          className="input text-sm w-full h-8"
                          placeholder={t('funnel.searchPlaceholder')}
                          value={contactSearch}
                          onChange={e => setContactSearch(e.target.value)}
                        />
                      </div>
                      <div className="max-h-52 overflow-y-auto">
                        <button type="button"
                          onClick={() => { setContact(''); setShowContactPicker(false); setContactSearch(''); }}
                          className={`w-full text-left px-3 py-2.5 text-xs transition-colors ${!contact ? 'bg-primary-50 text-primary-700 font-medium' : 'text-ink-tertiary hover:bg-surface-50'}`}>
                          {t('funnel.noContact')}
                        </button>
                        {filteredContacts.map(c => (
                          <button key={c._id} type="button"
                            onClick={() => { setContact(c._id); setShowContactPicker(false); setContactSearch(''); }}
                            className={`w-full text-left px-3 py-2.5 text-xs border-t border-surface-50 transition-colors flex items-center justify-between gap-2 ${contact === c._id ? 'bg-primary-50 text-primary-700' : 'hover:bg-surface-50 text-ink'}`}>
                            <span>
                              <span className="font-medium">{c.name}</span>
                              {c.phone && <span className="text-ink-tertiary ml-2">{c.phone}</span>}
                            </span>
                            {contact === c._id && <Check className="w-3.5 h-3.5 text-primary-500 shrink-0" />}
                          </button>
                        ))}
                        {filteredContacts.length === 0 && contactSearch && (
                          <p className="px-3 py-4 text-xs text-ink-tertiary text-center">Topilmadi</p>
                        )}
                      </div>
                      <div className="p-2 border-t border-surface-100">
                        {showNewContact ? (
                          <div className="space-y-2">
                            <input className="input text-xs" placeholder={t('funnel.contactNamePh')}
                              value={newContact.name} onChange={e => setNewContact(f => ({ ...f, name: e.target.value }))} autoFocus />
                            <input className="input text-xs" placeholder={t('funnel.contactPhonePh')}
                              value={newContact.phone} onChange={e => setNewContact(f => ({ ...f, phone: e.target.value }))} />
                            <input className="input text-xs" placeholder={t('funnel.contactEmailPh')}
                              value={newContact.email} onChange={e => setNewContact(f => ({ ...f, email: e.target.value }))} />
                            <div className="flex gap-2 pt-1">
                              <button type="button" onClick={() => { setShowNewContact(false); setNewContact({ name: '', phone: '', email: '' }); }}
                                className="flex-1 py-1.5 text-xs rounded-lg bg-surface-100 text-ink-secondary hover:bg-surface-200 transition-colors">
                                {t('deals.cancel')}
                              </button>
                              <button type="button" onClick={handleCreateContact} disabled={!newContact.name.trim() || savingContact}
                                className="flex-1 py-1.5 text-xs rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-1">
                                {savingContact ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                {t('deals.save')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" onClick={() => { setNewContact(f => ({ ...f, name: f.name || title })); setShowNewContact(true); }}
                            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-surface-300 text-xs text-ink-tertiary hover:border-primary-400 hover:text-primary-600 transition-colors">
                            <Plus className="w-3.5 h-3.5" />
                            {t('funnel.createContact')}
                          </button>
                        )}
                      </div>
                    </FloatingDropdown>
                  </div>

                  {/* Biriktirilgan kontakt ma'lumotlari */}
                  {linkedContactLoading && (
                    <div className="flex items-center gap-2 text-xs text-ink-tertiary px-4 pb-3">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Yuklanmoqda...
                    </div>
                  )}
                  {!linkedContactLoading && linkedContact && (
                    <div className="mx-4 mb-3 border border-surface-100 rounded-xl overflow-hidden">
                      <div className="divide-y divide-surface-100">
                        <div className="flex items-center gap-4 px-4 py-2.5">
                          <span className="w-28 text-sm text-ink-tertiary shrink-0">Kontakt</span>
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            {linkedContact.contactNumber && (
                              <span className="text-xs font-semibold text-ink-tertiary shrink-0">#{linkedContact.contactNumber}</span>
                            )}
                            <span className="text-sm font-medium text-ink truncate">{linkedContact.name}</span>
                            {linkedContact.blocked && (
                              <span className="text-[10px] font-medium text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full shrink-0">
                                Bloklangan
                              </span>
                            )}
                          </div>
                        </div>
                        {linkedContact.phone && (
                          <div className="flex items-center gap-4 px-4 py-2.5">
                            <span className="w-28 text-sm text-ink-tertiary shrink-0 flex items-center gap-1.5">
                              <Phone className="w-3.5 h-3.5" /> Telefon
                            </span>
                            <span className="flex-1 flex items-center justify-between gap-2 min-w-0">
                              <span className="text-sm text-ink">{linkedContact.phone}</span>
                              <CallButton phone={linkedContact.phone} iconClassName="w-3.5 h-3.5"
                                className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors shrink-0 disabled:opacity-50" />
                            </span>
                          </div>
                        )}
                        {linkedContact.email && (
                          <div className="flex items-center gap-4 px-4 py-2.5">
                            <span className="w-28 text-sm text-ink-tertiary shrink-0 flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5" /> Email
                            </span>
                            <span className="text-sm text-ink truncate">{linkedContact.email}</span>
                          </div>
                        )}
                        {linkedContact.reminderAt && new Date(linkedContact.reminderAt) < new Date() && (
                          <div className="flex items-center gap-4 px-4 py-2.5">
                            <span className="w-28 text-sm text-ink-tertiary shrink-0 flex items-center gap-1.5">
                              <AlertCircle className="w-3.5 h-3.5 text-red-500" /> Eslatma
                            </span>
                            <span className="text-sm text-red-500 font-medium">Muddati o'tdi</span>
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={() => navigate(`/contacts/${linkedContact._id}`)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-primary-600 hover:bg-primary-50 border-t border-surface-100 transition-colors">
                        <ExternalLink className="w-3.5 h-3.5" /> Kontaktni ochish
                      </button>
                    </div>
                  )}

                  {/* Assigned to */}
                  <div className="flex items-center gap-4 px-4 py-2.5">
                    <div className="flex items-center gap-2 w-32 shrink-0">
                      <User className="w-3.5 h-3.5 text-ink-tertiary" />
                      <span className="text-sm text-ink">{t('deals.assignee')}</span>
                    </div>
                    <button
                      ref={assignedAnchorRef}
                      type="button"
                      onClick={() => { setShowAssignedPicker(v => !v); setShowContactPicker(false); setContactSearch(''); }}
                      className="flex-1 flex items-center gap-2 min-w-0 hover:text-primary-600 transition-colors"
                    >
                      {assignedUser ? (
                        <>
                          <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                            <span className="text-[9px] font-bold text-primary-600">{initials(assignedUser.name)}</span>
                          </div>
                          <span className="text-sm text-ink font-medium truncate">{assignedUser.name}</span>
                        </>
                      ) : (
                        <span className="text-sm text-ink-disabled">— Tanlanmagan</span>
                      )}
                      <ChevronDown className={`w-3.5 h-3.5 text-ink-tertiary ml-auto shrink-0 transition-transform ${showAssignedPicker ? 'rotate-180' : ''}`} />
                    </button>
                    {!isNew && !assignedTo && canSave && (
                      <button
                        type="button"
                        onClick={handleClaim}
                        className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors shrink-0"
                      >
                        O'zimga olish
                      </button>
                    )}
                    <FloatingDropdown
                      anchorRef={assignedAnchorRef}
                      open={showAssignedPicker}
                      onClose={() => setShowAssignedPicker(false)}
                    >
                      <div className="max-h-56 overflow-y-auto">
                        <button type="button"
                          onClick={() => { setAssignedTo(''); setShowAssignedPicker(false); }}
                          className={`w-full text-left px-3 py-2.5 text-xs transition-colors ${!assignedTo ? 'bg-primary-50 text-primary-700 font-medium' : 'text-ink-tertiary hover:bg-surface-50'}`}>
                          — Tanlanmagan
                        </button>
                        {users.map(u => (
                          <button key={u._id} type="button"
                            onClick={() => { setAssignedTo(u._id); setShowAssignedPicker(false); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs border-t border-surface-50 transition-colors ${assignedTo === u._id ? 'bg-primary-50 text-primary-700' : 'hover:bg-surface-50 text-ink'}`}>
                            <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                              <span className="text-[9px] font-bold text-primary-600">{initials(u.name)}</span>
                            </div>
                            <span className="font-medium">{u.name}</span>
                            {assignedTo === u._id && <Check className="w-3.5 h-3.5 ml-auto text-primary-500 shrink-0" />}
                          </button>
                        ))}
                      </div>
                    </FloatingDropdown>
                  </div>
                </div>

                {/* Custom sections */}
                {orgSections.filter(s => s.fields.length > 0).map(sec => (
                  <div key={sec.id}>
                    <p className="text-[11px] font-semibold text-ink-tertiary uppercase tracking-wider mb-2 px-1">{sec.name}</p>
                    <div className="border border-surface-100 rounded-xl overflow-hidden">
                      <div className="divide-y divide-surface-100">
                        {sec.fields.map(field => (
                          <div key={field.id} className="flex items-center gap-4 px-4 py-2.5">
                            <span className="w-28 text-sm text-ink shrink-0">{field.key}</span>
                            <CustomFieldInput field={field} value={customFieldValues[field.id]}
                              onChange={val => setCustomFieldValues(prev => ({ ...prev, [field.id]: val }))} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ─── Statistika tab ─── */}
            {tab === 'stats' && (() => {
              const createdAt  = deal?.createdAt ? new Date(deal.createdAt) : null;
              const daysActive = createdAt ? Math.floor((Date.now() - createdAt) / 86400000) : 0;
              const fmt2 = d => d ? `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}` : '—';
              const noteCount  = activities.filter(a => a.type === 'note').length;
              const allCalls   = dealCalls.length;
              const inCount    = dealCalls.filter(c => c.direction === 'in').length;
              const outCount   = dealCalls.filter(c => c.direction === 'out').length;
              const missedCount = dealCalls.filter(c => c.status === 'missed').length;

              // Last 14 days activity chart
              const today = new Date(); today.setHours(0,0,0,0);
              const days14 = Array.from({ length: 14 }, (_, i) => {
                const d = new Date(today); d.setDate(d.getDate() - 13 + i);
                return d;
              });
              const actByDay = days14.map(day => {
                const nextDay = new Date(day); nextDay.setDate(nextDay.getDate() + 1);
                const count = activities.filter(a => {
                  const t = new Date(a.createdAt);
                  return t >= day && t < nextDay;
                }).length;
                return { day, count };
              });
              const maxAct = Math.max(...actByDay.map(d => d.count), 1);
              const BAR_H = 40;

              return (
                <div className="space-y-3">
                  {/* Dates row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-surface-100 rounded-xl px-3 py-2.5">
                      <p className="text-[10px] text-ink-tertiary mb-0.5">Yaratilgan</p>
                      <p className="text-sm font-semibold text-ink">{fmt2(createdAt)}</p>
                    </div>
                    <div className="border border-surface-100 rounded-xl px-3 py-2.5 flex flex-col items-center justify-center">
                      <span className="text-3xl font-black text-ink leading-none">{daysActive}</span>
                      <span className="text-[10px] text-ink-tertiary mt-0.5">kun davomida</span>
                    </div>
                  </div>

                  {/* 3-column KPIs */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="border border-surface-100 rounded-xl px-3 py-3 flex flex-col items-center gap-0.5">
                      <span className="text-xl font-bold text-ink">{allCalls}</span>
                      <span className="text-[10px] text-ink-tertiary text-center">Qo'ng'iroq</span>
                    </div>
                    <div className="border border-surface-100 rounded-xl px-3 py-3 flex flex-col items-center gap-0.5">
                      <span className="text-xl font-bold text-ink">{inCount}/{outCount}</span>
                      <span className="text-[10px] text-ink-tertiary text-center">Kir/Chiq</span>
                    </div>
                    <div className="border border-surface-100 rounded-xl px-3 py-3 flex flex-col items-center gap-0.5">
                      <span className="text-xl font-bold text-ink">{noteCount}</span>
                      <span className="text-[10px] text-ink-tertiary text-center">Izoh</span>
                    </div>
                  </div>

                  {missedCount > 0 && (
                    <div className="border border-amber-200 bg-amber-50 rounded-xl px-4 py-2.5 flex items-center justify-between">
                      <span className="text-xs text-amber-700">O'tkazib yuborilgan qo'ng'iroqlar</span>
                      <span className="text-sm font-bold text-amber-700">{missedCount}</span>
                    </div>
                  )}

                  {/* Activity bar chart (last 14 days) */}
                  <div className="border border-surface-100 rounded-xl px-4 py-3">
                    <p className="text-[10px] font-semibold text-ink-tertiary uppercase tracking-wider mb-3">Oxirgi 14 kun faolligi</p>
                    <svg width="100%" viewBox={`0 0 ${14 * 18} ${BAR_H + 16}`} className="overflow-visible">
                      {actByDay.map(({ day, count }, i) => {
                        const barH = count > 0 ? Math.max(4, Math.round((count / maxAct) * BAR_H)) : 3;
                        const x = i * 18 + 2;
                        const isToday = day.toDateString() === new Date().toDateString();
                        return (
                          <g key={i}>
                            <rect
                              x={x} y={BAR_H - barH} width={14} height={barH}
                              rx={3}
                              fill={count === 0 ? '#e2e8f0' : isToday ? '#6366f1' : '#a5b4fc'}
                            />
                            {(isToday || i % 4 === 0) && (
                              <text x={x + 7} y={BAR_H + 12} textAnchor="middle" fontSize={8} fill="#94a3b8">
                                {String(day.getDate()).padStart(2,'0')}/{String(day.getMonth()+1).padStart(2,'0')}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  </div>

                  {/* Qo'ng'iroqlar tarixi — shu lid kontaktiga bog'liq barcha qo'ng'iroqlar (yozuvlari bilan) */}
                  <div>
                    <p className="text-[10px] font-semibold text-ink-tertiary uppercase tracking-wider mb-2 px-1">Qo'ng'iroqlar tarixi</p>
                    {dealCalls.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center border border-surface-100 rounded-xl">
                        <Phone className="w-6 h-6 text-ink-disabled mb-2" />
                        <p className="text-xs text-ink-tertiary">Hali qo'ng'iroq yo'q</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {[...dealCalls]
                          .sort((a, b) => new Date(b.startedAt || b.createdAt) - new Date(a.startedAt || a.createdAt))
                          .map(c => <DealCallRow key={c._id} call={c} />)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ─── Fayllar tab ─── */}
            {tab === 'files' && (
              <div className="space-y-3">
                {!isNew && (
                  <label className={`w-full flex items-center justify-center gap-2.5 py-3 border-2 border-dashed border-surface-200 rounded-xl text-sm transition-colors cursor-pointer ${uploadingFile ? 'opacity-50 pointer-events-none' : 'hover:border-primary-300 hover:text-primary-600 text-ink-tertiary'}`}>
                    {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingFile ? 'Yuklanmoqda...' : '+ Fayl yuklash'}
                    <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploadingFile} />
                  </label>
                )}
                {isNew && (
                  <p className="text-xs text-ink-tertiary text-center py-4">Fayl yuklash uchun avval saqlab oling</p>
                )}
                {files.length === 0 && !isNew && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <FileText className="w-8 h-8 text-ink-disabled mb-2" />
                    <p className="text-sm text-ink-tertiary">Hali fayl yo'q</p>
                  </div>
                )}
                {files.map(f => (
                  <div key={f._id} className="flex items-center gap-3 p-3 border border-surface-100 rounded-xl group hover:bg-surface-50 transition-colors">
                    <FileText className="w-8 h-8 text-primary-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink truncate">{f.name}</p>
                      {f.size > 0 && <p className="text-xs text-ink-tertiary">{fmtSize(f.size)}</p>}
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <a href={`${API.replace('/api', '')}${f.url}?dl=1`} target="_blank" rel="noreferrer"
                        className="p-1.5 rounded hover:bg-primary-50 text-ink-tertiary hover:text-primary-600 transition-colors"
                        title="Yuklab olish">
                        <Upload className="w-3.5 h-3.5 rotate-180" />
                      </a>
                      <button onClick={() => handleFileDelete(f._id)}
                        className="p-1.5 rounded hover:bg-red-50 text-ink-tertiary hover:text-red-500 transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ─── Sozlama tab ─── */}
            {tab === 'settings' && (
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wider mb-3">Bo'limlar</p>
                  <div className="flex items-center justify-between py-2.5 px-3 bg-surface-50 rounded-lg mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink">Asosiy</span>
                      <span className="text-[10px] text-ink-tertiary bg-surface-100 px-1.5 py-0.5 rounded">Default</span>
                    </div>
                    <span className="text-xs text-ink-tertiary">Bosqich · Summa · Manba · Kontakt · Mas'ul</span>
                  </div>
                  {orgSections.map(sec => (
                    <div key={sec.id} className="flex items-center justify-between py-2.5 px-3 border border-surface-100 rounded-lg mb-2 group/sec">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-ink truncate">{sec.name}</span>
                        <span className="text-[10px] text-ink-tertiary shrink-0">{sec.fields.length} ta maydon</span>
                      </div>
                      <button onClick={() => deleteSection(sec.id)}
                        className="opacity-0 group-hover/sec:opacity-100 p-1 rounded text-ink-tertiary hover:text-red-500 transition-all shrink-0">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {addingSection ? (
                    <div className="flex items-center gap-2 py-2">
                      <input autoFocus className="input flex-1 text-sm" placeholder="Bo'lim nomi..."
                        value={newSectionName} onChange={e => setNewSectionName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') confirmAddSection(); if (e.key === 'Escape') { setAddingSection(false); setNewSectionName(''); } }} />
                      <button onClick={confirmAddSection} className="btn-sm btn-primary"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => { setAddingSection(false); setNewSectionName(''); }} className="btn-sm btn-secondary"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => setAddingSection(true)}
                      className="w-full flex items-center justify-center gap-2 py-2 border border-dashed border-surface-200 rounded-lg text-sm text-ink-tertiary hover:border-primary-300 hover:text-primary-600 transition-colors">
                      <Plus className="w-4 h-4" /> Yangi bo'lim qo'shish
                    </button>
                  )}
                </div>

                {orgSections.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-ink-tertiary uppercase tracking-wider mb-3">Maydonlar</p>
                    <div className="flex gap-1.5 flex-wrap mb-3">
                      {orgSections.map(sec => (
                        <button key={sec.id} onClick={() => { setActiveSectionId(sec.id); setAddingField(false); cancelEditField(); }}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${activeSectionId === sec.id ? 'bg-primary-500 text-white' : 'bg-surface-100 text-ink-secondary hover:bg-surface-200'}`}>
                          {sec.name}
                        </button>
                      ))}
                    </div>
                    {activeOrgSection && (
                      <div className="space-y-1">
                        {activeOrgSection.fields.length === 0 && !addingField && (
                          <p className="text-xs text-ink-tertiary py-2">Hali maydon yo'q</p>
                        )}
                        {activeOrgSection.fields.map(field => (
                          editingFieldId === field.id ? (
                            <div key={field.id} className="py-2.5 border-b border-surface-50 space-y-2.5">
                              <div className="flex gap-2">
                                <input
                                  autoFocus
                                  className="input flex-1 text-sm"
                                  placeholder="Maydon nomi..."
                                  value={editField.key}
                                  onChange={e => setEditField(f => ({ ...f, key: e.target.value }))}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && !NEEDS_OPTIONS.includes(editField.type)) confirmEditField(activeOrgSection.id);
                                    if (e.key === 'Escape') cancelEditField();
                                  }}
                                />
                                <div className="relative w-36 shrink-0">
                                  <select className="input appearance-none pr-7 text-sm"
                                    value={editField.type}
                                    onChange={e => setEditField(f => ({ ...f, type: e.target.value, options: NEEDS_OPTIONS.includes(e.target.value) ? f.options : [] }))}>
                                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                  </select>
                                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary pointer-events-none" />
                                </div>
                              </div>

                              {NEEDS_OPTIONS.includes(editField.type) && (
                                <div className="pl-1 space-y-1.5">
                                  <p className="text-xs text-ink-tertiary">Variantlar {editField.options.length === 0 && <span className="text-red-400">(kamida 1 ta)</span>}</p>
                                  {editField.options.map((opt, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <input
                                        className="input flex-1 text-sm"
                                        value={opt}
                                        onChange={e => setEditField(f => ({ ...f, options: f.options.map((o, j) => j === i ? e.target.value : o) }))}
                                      />
                                      <button
                                        onClick={() => setEditField(f => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}
                                        className="p-1 text-ink-tertiary hover:text-red-500 transition-colors"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                  <div className="flex gap-2">
                                    <input
                                      className="input flex-1 text-sm"
                                      placeholder="Variant nomi..."
                                      value={editOption}
                                      onChange={e => setEditOption(e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          if (editOption.trim()) {
                                            setEditField(f => ({ ...f, options: [...f.options, editOption.trim()] }));
                                            setEditOption('');
                                          }
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        if (editOption.trim()) {
                                          setEditField(f => ({ ...f, options: [...f.options, editOption.trim()] }));
                                          setEditOption('');
                                        }
                                      }}
                                      className="btn-sm btn-secondary shrink-0"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>
                              )}

                              <div className="flex gap-2">
                                <button onClick={() => confirmEditField(activeOrgSection.id)} className="btn-sm btn-primary flex-1">
                                  <Check className="w-3.5 h-3.5" /> Saqlash
                                </button>
                                <button onClick={cancelEditField} className="btn-sm btn-secondary flex-1">
                                  Bekor
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div key={field.id} className="flex items-center gap-3 py-2 border-b border-surface-50 group/field">
                              <span className="flex-1 text-sm text-ink">{field.key}</span>
                              <span className="text-xs text-ink-tertiary bg-surface-100 px-2 py-0.5 rounded-full shrink-0">
                                {FIELD_TYPES.find(t => t.value === field.type)?.label}
                              </span>
                              <button onClick={() => startEditField(field)}
                                className="shrink-0 opacity-0 group-hover/field:opacity-100 p-1 rounded text-ink-tertiary hover:text-primary-600 transition-all">
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => deleteCustomField(activeOrgSection.id, field.id)}
                                className="shrink-0 opacity-0 group-hover/field:opacity-100 p-1 rounded text-ink-tertiary hover:text-red-500 transition-all">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )
                        ))}
                        {addingField ? (
                          <div className="pt-3 space-y-2.5">
                            <div className="flex gap-2">
                              <input autoFocus className="input flex-1 text-sm" placeholder="Maydon nomi..."
                                value={newField.key} onChange={e => setNewField(f => ({ ...f, key: e.target.value }))}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && !NEEDS_OPTIONS.includes(newField.type)) confirmAddField();
                                  if (e.key === 'Escape') { setAddingField(false); setNewField({ key: '', type: 'text', options: [] }); setNewOption(''); }
                                }} />
                              <div className="relative w-36 shrink-0">
                                <select className="input appearance-none pr-7 text-sm"
                                  value={newField.type} onChange={e => setNewField(f => ({ ...f, type: e.target.value, options: [] }))}>
                                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-tertiary pointer-events-none" />
                              </div>
                            </div>
                            {NEEDS_OPTIONS.includes(newField.type) && (
                              <div className="pl-1 space-y-1.5">
                                <p className="text-xs text-ink-tertiary">Variantlar {newField.options.length === 0 && <span className="text-red-400">(kamida 1 ta)</span>}</p>
                                {newField.options.map((opt, i) => (
                                  <div key={i} className="flex items-center gap-2">
                                    <span className="flex-1 text-sm text-ink bg-surface-50 px-2.5 py-1 rounded-lg">{opt}</span>
                                    <button onClick={() => setNewField(f => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}
                                      className="p-1 text-ink-tertiary hover:text-red-500 transition-colors"><X className="w-3.5 h-3.5" /></button>
                                  </div>
                                ))}
                                <div className="flex gap-2">
                                  <input className="input flex-1 text-sm" placeholder="Variant nomi..."
                                    value={newOption} onChange={e => setNewOption(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newOption.trim()) { setNewField(f => ({ ...f, options: [...f.options, newOption.trim()] })); setNewOption(''); } } }} />
                                  <button onClick={() => { if (newOption.trim()) { setNewField(f => ({ ...f, options: [...f.options, newOption.trim()] })); setNewOption(''); } }}
                                    className="btn-sm btn-secondary shrink-0"><Plus className="w-3.5 h-3.5" /></button>
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button onClick={confirmAddField} className="btn-sm btn-primary flex-1"><Check className="w-3.5 h-3.5" /> Qo'shish</button>
                              <button onClick={() => { setAddingField(false); setNewField({ key: '', type: 'text', options: [] }); setNewOption(''); }}
                                className="btn-sm btn-secondary flex-1">Bekor</button>
                            </div>
                          </div>
                        ) : (
                          <button onClick={() => { setAddingField(true); cancelEditField(); }}
                            className="mt-2 flex items-center gap-1.5 text-xs text-ink-tertiary hover:text-primary-600 transition-colors py-2">
                            <Plus className="w-3.5 h-3.5" /> Maydon qo'shish
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Activity feed (telefonda pastda joylashadi) ── */}
        <div className="w-full lg:flex-1 flex flex-col lg:min-w-0 bg-surface-50">
          <div className="px-5 py-3 border-b border-surface-100 bg-white shrink-0 flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-ink">{t('contactForm.tabActivity')}</p>
              <p className="text-xs text-ink-tertiary mt-0.5">{t('deals.activitySub')}</p>
            </div>
            {!isNew && canCreateTask && (
              <button onClick={openTaskModal}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border border-surface-200 text-ink-secondary hover:border-primary-300 hover:text-primary-600 transition-colors shrink-0">
                <CheckSquare2 className="w-3.5 h-3.5" /> {t('tasks.newTask')}
              </button>
            )}
          </div>

          {/* Shu lidga bog'langan vazifalar */}
          {!isNew && (dealTasksLoading || dealTasks.length > 0) && (
            <div className="px-5 py-3 bg-white border-b border-surface-100 shrink-0 space-y-1.5">
              {dealTasksLoading ? (
                <div className="flex items-center justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-ink-tertiary" /></div>
              ) : dealTasks.map(dt => (
                <div key={dt._id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-surface-50 group">
                  <CheckSquare2 className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                  <span className="text-xs text-ink truncate flex-1">{dt.title}</span>
                  {dt.assignedTo?.name && (
                    <span className="text-[10px] text-ink-tertiary shrink-0">{dt.assignedTo.name}</span>
                  )}
                  {dt.dueDate && (
                    <span className="text-[10px] text-ink-tertiary shrink-0 flex items-center gap-0.5">
                      <Calendar className="w-3 h-3" />{new Date(dt.dueDate).toLocaleDateString('uz-UZ')} {new Date(dt.dueDate).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {canDeleteTask && (
                    <button onClick={() => handleDeleteTask(dt._id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-md text-ink-disabled hover:text-red-500 hover:bg-red-50 transition-colors shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="min-h-[40vh] lg:min-h-0 lg:flex-1 lg:overflow-y-auto px-5 py-4">
            {isNew ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-14 h-14 bg-white border border-surface-100 rounded-2xl flex items-center justify-center mb-3">
                  <MessageSquare className="w-7 h-7 text-ink-disabled" />
                </div>
                <p className="text-sm font-medium text-ink-secondary">{t('deals.activityEmpty')}</p>
                <p className="text-xs text-ink-tertiary mt-1 max-w-[200px]">{t('deals.activityHint')}</p>
              </div>
            ) : actLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              </div>
            ) : (activities.length === 0 && dealCalls.length === 0) ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-14 h-14 bg-white border border-surface-100 rounded-2xl flex items-center justify-center mb-3">
                  <MessageSquare className="w-7 h-7 text-ink-disabled" />
                </div>
                <p className="text-sm font-medium text-ink-secondary">{t('deals.activityEmpty')}</p>
                <p className="text-xs text-ink-tertiary mt-1">{t('deals.activityHint')}</p>
              </div>
            ) : (() => {
              // Izohlar/bosqich o'zgarishlari va qo'ng'iroqlarni bitta xronologik oqimga aralashtiramiz
              const actItems  = activities.map(a => ({ ...a, _feedType: 'activity', _ts: new Date(a.createdAt).getTime() }));
              const callItems = dealCalls.map(c => ({ ...c, _feedType: 'call', _ts: new Date(c.startedAt || c.createdAt).getTime() }));
              const merged = [...actItems, ...callItems].sort((a, b) => a._ts - b._ts);
              return (
                <div className="space-y-0.5">
                  {merged.map(item =>
                    item._feedType === 'call'
                      ? <CallItem key={`call-${item._id}`} call={item} />
                      : item.type === 'note'
                      ? <NoteItem key={item._id} activity={item} onDelete={handleDeleteNote} currentUserId={meId} />
                      : item.type === 'communication'
                      ? <CommunicationItem key={item._id} activity={item} onDelete={handleDeleteNote} currentUserId={meId} />
                      : <SystemEvent key={item._id} activity={item} />
                  )}
                  <div ref={bottomRef} />
                </div>
              );
            })()}
          </div>
          {!isNew && (
            <div className="shrink-0 border-t border-surface-100 bg-white px-4 py-3">
              <div className="flex items-center gap-1 bg-surface-100 rounded-xl p-1 w-fit mb-2">
                <button type="button" onClick={() => setNoteMode('note')}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${noteMode === 'note' ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
                  {t('deals.noteTab')}
                </button>
                <button type="button" onClick={() => setNoteMode('communication')}
                  className={`px-3 py-1 text-xs font-medium rounded-lg transition-all ${noteMode === 'communication' ? 'bg-white text-ink shadow-sm' : 'text-ink-tertiary hover:text-ink'}`}>
                  {t('deals.communicationTab')}
                </button>
              </div>

              {noteMode === 'communication' && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {['call', 'message', 'meeting'].map(ct => {
                    const Icon = CONTACT_TYPE_ICON[ct];
                    return (
                      <button key={ct} type="button" onClick={() => setCommContactType(ct)}
                        className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${commContactType === ct ? 'bg-primary-500 border-primary-500 text-white' : 'bg-transparent border-surface-200 text-ink-secondary hover:border-primary-300'}`}>
                        <Icon className="w-3 h-3" /> {t(contactTypeLabelKey(ct))}
                      </button>
                    );
                  })}
                  {commContactType === 'call' && (
                    <input type="number" min="0" placeholder={t('deals.durationLabel')} value={commDuration}
                      onChange={e => setCommDuration(e.target.value)}
                      className="w-24 input text-xs h-7" />
                  )}
                  <div className="w-full flex flex-wrap gap-1.5 mt-1">
                    {OUTCOME_TAGS.map(tag => (
                      <button key={tag} type="button" onClick={() => setCommOutcome(commOutcome === tag ? '' : tag)}
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full border transition-colors ${commOutcome === tag ? (OUTCOME_COLOR[tag] || 'bg-primary-500 text-white') + ' border-transparent' : 'bg-transparent border-surface-200 text-ink-tertiary hover:border-primary-300'}`}>
                        {t(outcomeLabelKey(tag))}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-end bg-surface-50 rounded-xl border border-surface-200 focus-within:border-primary-300 focus-within:bg-white focus-within:shadow-sm transition-all duration-200">
                <textarea ref={textareaRef}
                  className="flex-1 min-w-0 bg-transparent text-sm text-ink placeholder:text-ink-tertiary resize-none border-0 outline-none focus:outline-none focus:ring-0 leading-relaxed px-4 py-3"
                  placeholder={t('deals.notePlaceholder')} rows={3} value={noteText}
                  onChange={e => { setNoteText(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendNote(); } }} />
                <div className="shrink-0 p-2 transition-all duration-150"
                  style={{ opacity: noteText.trim() ? 1 : 0, transform: noteText.trim() ? 'scale(1)' : 'scale(0.7)', pointerEvents: noteText.trim() ? 'auto' : 'none' }}>
                  <button onClick={handleSendNote} disabled={noteSending}
                    className="w-8 h-8 rounded-xl bg-primary-500 text-white flex items-center justify-center hover:bg-primary-400 active:scale-95 transition-all">
                    {noteSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              {noteText && <p className="text-[10px] text-ink-tertiary mt-1.5 px-1">Shift+Enter — yangi qator</p>}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
